import {
  addDoc,
  collection,
  collectionGroup,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  getCountFromServer,
} from "firebase/firestore";
import { db } from "../firebase";
import { Company } from "../model/Companies";
import { Groups } from "../model/Groups";
import { OnlineUrls } from "../model/OnlineUrls";
import { ReplyButton } from "../model/ReplyButton";
import { LinkMenus } from "../model/LinkMenus";
import { SystemMenus } from "../model/SystemMenus";
import { VideoMenus } from "../model/VideoMenus";
import { MenuSettings } from "../model/MenuSettings";
import { VideoContents, VideoContentsTimestampToDate } from "../model/VideoContents";
import { SystemSetting } from "../model/SystemSetting";
import { Consent, consentTimestampToDate } from "../model/Consent";
import { User } from "../model/Users";
import { OptionContract, optionContractTimestampToDate } from "../model/OptionContract";

type Response = {
  success: boolean;
  data: any;
}

const companiesQuery = {
  //団体リストを取得
  getCompaniesList: async () => {
    console.log("companies getCompaniesList access");
    const companiesRef = await getDocs(collection(db, "companies"));
    return companiesRef.docs.map((doc) => doc.data() as Company);
  },
  // 団体数取得
  getCompaniesCount: async (showDeleted: boolean) => {
    let q = query(collection(db, "companies"));
    if (!showDeleted) {
      q = query(collection(db, "companies"), where("deleteFlg", "==", false));
    }
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  },
  //指定した団体を取得
  getCompany: async (tenantId: string) => {
    try {
      console.log("companies getCompany access");
      const companiesRef = await getDoc(doc(db, "companies", tenantId));
      return companiesRef.data() as Company;
    } catch (err: any) {
      console.error(err);
      return err;
    }
  },
  // 指定したdisplayIdを持つ団体を取得
  getDisplayIdCompanies: async (displayId: string) => {
    console.log("companies getDisplayIdCompanies access");
    const companiesRef = await getDocs(collection(db, "companies"));
    return companiesRef.docs.filter((doc) => {
      const company = doc.data() as Company;
      return company.displayId === displayId;
    })
  },
  queryUpdateCompanies: async (company: Company) => {
    console.log("companies queryUpdateCompanies access");
    try {
      const comanyRef = doc(db, "companies", company.id);
      await updateDoc(comanyRef, {
        usersCount: company.usersCount,
        name: company.name,
        nameKana: company.nameKana,
        managerEmail: company.managerEmail,
        managerName: company.managerName,
        managerNameKana: company.managerNameKana,
        updateAt: serverTimestamp(),
      });
    } catch (err) {
      console.error(err);
    }
  },
  changesCompaniesState: async (company: Company) => {
    console.log("companies changesCompaniesState access");
    try {
      const comanyRef = doc(db, "companies", company.id);
      await updateDoc(comanyRef, {
        usersCount: company.usersCount,
        name: company.name,
        nameKana: company.nameKana,
        managerEmail: company.managerEmail,
        managerName: company.managerName,
        managerNameKana: company.managerNameKana,
        deleteFlg: !company.deleteFlg,
        updateAt: serverTimestamp(),
      });
    } catch (err) {
      console.error(err);
    }
  },
  // 団体別にグループ取得
  getTenantGroups: async (tenantId: string): Promise<Groups[]> => {
    console.log("companies getTenantGroup access");
    try {
      const groupRef = await getDocs(collection(db, `companies/${tenantId}/groups`));
      return groupRef.docs.map((doc) => doc.data() as Groups);
    } catch (err: any) {
      console.error(err);
      return [];
    }
  },
  // 返信ボタン一覧取得
  getReplyButtons: async (companyId: string): Promise<ReplyButton[]> => {
    console.log("companies getReplyButtons access");
    const replyButtonsRef = await getDocs(
      query(collection(db, `companies/${companyId}/replyButtons`), orderBy('createAt', 'asc')));
    const replyButtons = replyButtonsRef.docs.map(
      (replyButton) => replyButton.data() as ReplyButton);
    return replyButtons;
  },
  // 返信ボタン作成
  setReplyButton: async (companyId: string, replyButton: ReplyButton): Promise<void> => {
    console.log("companies setReplyButtons access");
    try {
      const resolve = await addDoc(
        collection(db, `companies/${companyId}/replyButtons`),
        replyButton);
      await updateDoc(resolve, {id: resolve.id});
    } catch (err: any) {
      console.error(err);
    }
  },
  // 返信ボタン更新
  updateReplyButton: async (companyId: string, replyButton: ReplyButton): Promise<void> => {
    console.log("companies updateReplyButtons access");
    try {
      await updateDoc(
        doc(db, `companies/${companyId}/replyButtons/${replyButton.id}`),
        replyButton);
    } catch (err: any) {
      console.error(err);
    }
  },
  // 返信ボタン削除
  deleteReplyButton: async (companyId: string, documentId: string): Promise<void> => {
    console.log("companies deleteReplyButton access");
    try {
      await deleteDoc(doc(db, `companies/${companyId}/replyButtons/${documentId}`));
    } catch (err: any) {
      console.error(err);
    }
  },
  // ミーティングURL一覧取得
  getOnlineUrls: async (companyId?: string | undefined): Promise<OnlineUrls[]> => {
    console.log("companies getOnlineUrls access");
    const onlineUrlsQuery = companyId !== undefined
      ? query(collection(db, `companies/${companyId}/onlineUrls`), ...[orderBy('type', 'asc'), orderBy('name', 'asc')])
      : query(collectionGroup(db, 'onlineUrls'), ...[orderBy('type', 'asc'), orderBy('name', 'asc')])
    const onlineUrlsRef = await getDocs(onlineUrlsQuery);
    const onlineUrls = onlineUrlsRef.docs.map((doc):OnlineUrls => {
      const companyId = doc.ref.parent.parent?.id;
      return {
        id: doc.data().id,
        companyId: companyId,
        onlineType: doc.data().onlineType,
        name: doc.data().name,
        url: doc.data().url,
        type: doc.data().type,
        createUser: doc.data().createUser,
        createAt: doc.data().createAt,
      }
    });
    return onlineUrls;
  },
  // ミーティングURL登録
  setOnlineUrl: async (companyId: string, onlineUrl: OnlineUrls): Promise<void> => {
    console.log("companies setOnlineUrl access");
    try {
      const resolve = await addDoc(
        collection(db, `companies/${companyId}/onlineUrls`),
        onlineUrl);
      await updateDoc(resolve, {id: resolve.id});
    } catch (err: any) {
      console.error(err);
    }
  },
  // ミーティングURL更新
  updateOnlineUrl: async (companyId: string, onlineUrl: OnlineUrls): Promise<void> => {
    console.log("companies updateOnlineUrl access");
    try {
      await updateDoc(
        doc(db, `companies/${companyId}/onlineUrls/${onlineUrl.id}`),
        onlineUrl);
    } catch (err: any) {
      console.error(err);
    }
  },
  // ミーティングURL削除
  deleteOnlineUrl: async (companyId: string, documentId: string): Promise<void> => {
    console.log("companies deleteOnlineUrl access");
    try {
      await deleteDoc(doc(db, `companies/${companyId}/onlineUrls/${documentId}`));
    } catch (err: any) {
      console.error(err);
    }
  },
  // メニュー設定取得
  getMenuSettings: async (companyId: string): Promise<MenuSettings[]> => {
    console.log("companies getMenuSettings access");
    const menuSettingsRef = await getDocs(
      query(
        collection(db, `companies/${companyId}/menuSettings`),
        orderBy('displayOrder', 'asc')
      )
    );
    return menuSettingsRef.docs.map((doc) => doc.data() as MenuSettings);
  },
  // メニュー設定一覧登録・更新
  upsertMenuSettings: async (companyId: string, menuSettings: MenuSettings[]):
    Promise<MenuSettings[]> => {
    console.log("companies upsertMenuSettings access");
    try {
      const resolve = await Promise.all(menuSettings.map(async (menu) => {
        return await companiesQuery.upsertMenuSetting(companyId, menu);
      }));
      return resolve.sort((a, b) => Number(a.displayOrder) - Number(b.displayOrder));
    } catch (err: any) {
      console.error(err);
      return [];
    }
  },
  // メニュー設定登録・更新
  upsertMenuSetting: async (companyId: string, menuSetting: MenuSettings):
    Promise<MenuSettings> => {
    console.log("companies upsertMenuSetting access");
    try {
      if(menuSetting.id === '' || menuSetting.id === undefined){
        const setResolve = await addDoc(
          collection(db, `companies/${companyId}/menuSettings`),
          menuSetting);
        await updateDoc(setResolve, {id: setResolve.id})
        const doc = await getDoc(setResolve);
        return {
          id: setResolve.id,
          displayOrder: doc.data()?.displayOrder,
          enableFlag: doc.data()?.enableFlag,
          menuType: doc.data()?.menuType,
          menuNo: doc.data()?.menuNo,
        } as MenuSettings;
      } else {
        await updateDoc(
          doc(db, `companies/${companyId}/menuSettings/${menuSetting.id}`),
          menuSetting);
        return menuSetting;
      }
    } catch (err) {
      console.error(err);
      return {
        id: "",
        displayOrder: "0",
        enableFlag: false,
        menuType: undefined,
        menuNo: "",
      }
    }
  },
  // メニュー設定削除
  deleteMenuSetting: async (companyId: string, menuId: string):
    Promise<boolean> => {
    console.log("companies deleteMenuSetting access");
    try {
      await deleteDoc(doc(db, `companies/${companyId}/menuSettings/${menuId}`));
      return true;
    } catch (err: any) {
      return false;
    }
  },
  // メニューNoからメニュー設定削除
  deleteMenuSettingByMenuNo: async (companyId: string, menuNo: string):
    Promise<boolean> => {
    console.log("companies deleteMenuSettingByMenuNo access");
    try {
      const docRef = await getDocs(query(collection(db, `companies/${companyId}/menuSettings`), where('menuNo', '==', menuNo)));
      await deleteDoc(doc(db, `companies/${companyId}/menuSettings/${docRef.docs[0].id}`));
      return true;
    } catch(err: any) {
      return false;
    }
  },
  // システム設定取得
  getSystemSetting: async (companyId: string):
    Promise<SystemSetting> => {
    console.log("companies getSystemSetting access");
    const docRef = await getDocs(collection(db, `companies/${companyId}/systemSetting`));
    return docRef.docs.map((doc) => doc.data() as SystemSetting)[0];
  },
  // システム設定登録・更新
  upsertSystemSetting: async (companyId: string, systemSetting: SystemSetting):
    Promise<SystemSetting | any> => {
    console.log("companies upsertSystemSetting access");
    try {
      const isUpdate = (systemSetting.id !== undefined && systemSetting.id !== '');
      const updateValue = {
        id: systemSetting.id ?? '',
        subTitle: systemSetting.subTitle ?? deleteField(),
        image: systemSetting.image ?? deleteField(),
        colorTheme: systemSetting.colorTheme ?? deleteField(),
        mypageDispFlg: systemSetting.mypageDispFlg ?? deleteField(),
        codeDispFlg: systemSetting.codeDispFlg ?? deleteField()
      };
      const docRef = isUpdate
        ? doc(db, `companies/${companyId}/systemSetting/${systemSetting.id}`)
        : await addDoc(collection(db, `companies/${companyId}/systemSetting`), systemSetting);
      await updateDoc(docRef, isUpdate ? updateValue : {id: docRef.id});
      const result = await getDoc(docRef);
      return result.data() as SystemSetting;
    } catch(err: any) {
      console.error(err);
      return err;
    }
  },
  // システムメニュー一覧取得
  getSystemMenusList: async (companyId: string):
    Promise<SystemMenus[]> => {
    console.log("companies getSystemMenusList access");
    const systemMenusRef = await getDocs(
      collection(db, `companies/${companyId}/systemMenus`));
    return systemMenusRef.docs.map((doc) => doc.data() as SystemMenus);
  },
  // システムメニュー取得
  getSystemMenus: async (companyId: string, id: string):
    Promise<SystemMenus> => {
    console.log("companies getSystemMenus access");
    const systemMenusRef = await getDoc(
      doc(db, `companies/${companyId}/systemMenus/${id}`));
    return systemMenusRef.data() as SystemMenus;
  },
  // システムメニュー登録
  setSystemMenus: async (companyId: string, systemMenu: SystemMenus):
    Promise<SystemMenus> => {
    console.log("companies setSystemMenus access");
    try {
      const result = await addDoc(collection(db, `companies/${companyId}/systemMenus`), systemMenu);
      await updateDoc(result, {id: result.id});
      const systemMenusRef = await getDoc(result);
      return systemMenusRef.data() as SystemMenus;
    } catch (err: any) {
      console.error(err);
      return {
        id: "error",
        type: "notice",
        name: "error"
      }
    }
  },
  // システムメニュー更新
  updateSystemMenus: async (companyId: string, systemMenu: SystemMenus):
    Promise<void> => {
    console.log("companies updateSystemMenus access");
    try {
      await updateDoc(
        doc(db, `companies/${companyId}/systemMenus/${systemMenu.id}`),
        systemMenu);
    } catch (err: any){
      console.error(err);
    }
  },
  // 動画メニュー一覧取得
  getVideoMenusList: async (companyId: string):
    Promise<VideoMenus[]> => {
    console.log("companies getVideoMenusList access");
    const videoMenusRef = await getDocs(
      collection(db, `companies/${companyId}/videoMenus`));
    return videoMenusRef.docs.map((doc) => doc.data() as VideoMenus);
  },
  // 動画メニュー取得
  getVideoMenus: async (companyId: string, videoMenuId: string):
    Promise<VideoMenus> => {
    console.log("companies getVideoMenus access");
    const videoMenusRef = await getDoc(
      doc(db, `companies/${companyId}/videoMenus/${videoMenuId}`));
    return videoMenusRef.data() as VideoMenus;
  },

  // 動画コンテンツ一覧取得
  getVideoContentsList: async ():
    Promise<VideoContents[]> => {
    console.log("companies VideoContents access");
    const videoContentsRef = await getDocs(
        collection(db, `systemParameters/00000001/videoContents`));
    return videoContentsRef.docs.map((doc) =>
      VideoContentsTimestampToDate(doc.data()) as VideoContents);
  },
  // 動画コンテンツ一覧登録・更新
  upsertVideoContent: async (
    videoContents: VideoContents
  ): Promise<Response> => {
    console.log("companies upsertVideoContent access");
    try {
      if(videoContents.id === '' || videoContents.id === undefined){
        const setResolve = await addDoc(
          collection(db, `systemParameters/00000001/videoContents`),
          videoContents);
        await updateDoc(setResolve, {id: setResolve.id})
        const docRef = await getDoc(doc(db, `systemParameters/00000001/videoContents/${setResolve.id}`));
        return {success: true, data: docRef.data() as VideoContents};
      } else {
        await updateDoc(
          doc(db, `systemParameters/00000001/videoContents/${videoContents.id}`),
          videoContents);
        return {success: true, data: videoContents};
      }
    } catch (err) {
      console.error(err);
      return {success: false, data: err}
    }
  },
  // 動画コンテンツ削除
  deleteVideoContents: async (documentId: string):
    Promise<Response> => {
    console.log("companies deleteVideoContent access");
    try {
      await deleteDoc(doc(db, `systemParameters/00000001/videoContents/${documentId}`));
      return {success: true, data: documentId};
    } catch (err: any) {
      console.error(err);
      return {success: false, data: err};
    }
  },
  // 動画コンテンツを団体に登録
  setVideoContentsIntoCompanies: async (data: VideoContents):
    Promise<Response> => {
    console.log("companies setVideoContentsIntoCompanies access");
    try {
      const copyData = {...data};
      delete data.companies;
      const result = copyData.companies?.map(async (id) => {
        const docRef = await setDoc(
          doc(db, `companies/${id}/videoContents/${data.id}`),
          data);
        return docRef;
      });
      return {success: true, data: result};
    } catch (err: any) {
      return {success: false, data: err};
    }
  },
  // 動画コンテンツを団体から削除
  deleteVideoContentsIntoCompanies: async (documentId: string):
    Promise<Response> => {
    console.log("companies deleteVideoContent access");
    try {
      const docsRef = await getDocs(
        query(collectionGroup(db, 'videoContents'),
          where('id', '==', documentId)
        )
      );
      const result = docsRef.docs.map(async (docSnap) => {
        const companyId = docSnap.ref.parent.parent?.id;
        return await deleteDoc(doc(db, `companies/${companyId}/videoContents/${docSnap.id}`));
      });
      return {success: true, data: result};
    } catch (err: any) {
      console.error(err);
      return {success: false, data: err};
    }
  },
  // 連携アプリ一覧取得
  getLinkMenusList: async (companyId: string): Promise<LinkMenus[]> => {
    console.log("companies getLinkMenusList access");
    const linkMenusRef = await getDocs(
      query(collection(db, `companies/${companyId}/linkMenus/`),
      ...[orderBy('type', 'asc'), orderBy('name', 'asc')]));
    const linkMenusData = linkMenusRef.docs.map((item) => item.data() as LinkMenus);
    return linkMenusData;
  },
  // 連携アプリ取得
  getLinkMenus: async (companyId: string, linkMenusId: string): Promise<LinkMenus> => {
    console.log("companies getLinkMenus access");
    const linkMenusRef = await getDoc(doc(db, `companies/${companyId}/linkMenus/${linkMenusId}`));
    return linkMenusRef.data() as LinkMenus;
  },
  // 連携アプリ登録
  setLinkMenus: async (companyId: string, linkMenus: LinkMenus): Promise<void> => {
    console.log("companies setLinkMenus access");
    try {
      const resolve = await addDoc(
        collection(db, `companies/${companyId}/linkMenus`),
        linkMenus);
      await updateDoc(resolve, {id: resolve.id});
    } catch (err: any) {
      console.error(err);
    }
  },
  // 連携アプリ更新
  updateLinkMenus: async (companyId: string, linkMenus: LinkMenus): Promise<void> => {
    console.log("companies updateLinkMenus access");
    try {
      await updateDoc(
        doc(db, `companies/${companyId}/linkMenus/${linkMenus.id}`),
        linkMenus
      );
    } catch (err: any) {
      console.error(err)
    }
  },
  // 連携アプリ削除
  deleteLinkMenus: async (companyId: string, linkMenus: LinkMenus): Promise<void> => {
    console.log("companies deleteLinkMenus access");
    try {
      await deleteDoc(doc(db, `companies/${companyId}/linkMenus/${linkMenus.id}`));
    } catch (err: any) {
      console.error(err)
    }
  },
  // 利用規約・個人情報一覧取得
  getConsentList: async (): Promise<Response> => {
    console.log("companies getConsentList access");
    try {
      const consentRef = await getDocs(query(collection(db,'consent'),
        ...[
          orderBy('appType', 'asc'),
          orderBy('version', 'desc'),
          orderBy('type', 'asc')
        ]));
      return {success: true, data: consentRef.docs.map((consent) => consentTimestampToDate(consent.data()) as Consent)};
    } catch (err: any) {
      console.error(err);
      return {success: false, data: err};
    }
  },
  // 利用規約・個人情報登録
  addConsent: async (consent: Consent): Promise<Response> => {
    console.log("companies addConsent access");
    try {
      consent.updateAt = new Date();
      const docRef = await addDoc(collection(db, 'consent'), consent);
      const result = await getDoc(doc(db, `consent/${docRef.id}`));
      return {success: true, data: result.data() as Consent};
    } catch (err: any) {
      console.error(err);
      return {success: false, data: err};
    }
  },
  // 利用規約・個人情報更新
  updateConsent: async (consent: Consent): Promise<Response> => {
    console.log("companies updateConsent access");
    try {
      const docsRef = await getDocs(query(collection(db, 'consent'),
        ...[
          where('version', '==', consent.version),
          where('appType', '==', consent.appType),
          where('type', '==', consent.type),
          orderBy('updateAt', 'desc')
        ]));
      if(docsRef.docs.length > 0){
        consent.updateAt = new Date();
        const result = await updateDoc(doc(db, `consent/${docsRef.docs[0].id}`), consent);
        return {success: true, data: result};
      } else {
        const result =  await companiesQuery.addConsent(consent);
        return result;
      }
    } catch (err: any) {
      console.error(err);
      return {success: false, data: err};
    }
  },

  // 運営者数の取得
  getAdminUser: async (companyId: string): Promise<User[]> => {
    console.log("companies getAdminUser access");
    const userRef = await getDocs(
      query(collection(db, `companies/${companyId}/users`),
        where('admin', '==', true),
        where('accountSuspensionFlg', '==', false)));
    const user = await userRef.docs.map(
      (user) => user.data() as User);
    return user;
  },

  // 運営者数の取得
  getAdminUserCount: async (companyId: string): Promise<number> => {
    console.log("companies getAdminUserCount access");
    const ref = await getCountFromServer(
      query(
        collection(db, `companies/${companyId}/users`),
        where('admin', '==', true),
        where('accountSuspensionFlg', '==', false)
      )
    );
    return ref.data().count;
  },

  // オプション契約一覧を取得
  getOptionContractList: async (tenantId: string): Promise<OptionContract[]> => {
    console.log("companies getOptionContractList access");
    const optionContractList: OptionContract[] = [];
    const option = await getDocs(collection(db, `companies/${tenantId}/optionContract`));
    option.docs.forEach((doc) => {
      const targetData = doc.data();
      optionContractList.push(optionContractTimestampToDate(targetData));
    });
    return optionContractList;
  },
  // オプション契約新規登録
  setOptionContract: async (companyId: string, optionContract: OptionContract): Promise<void> => {
    console.log("companies setOptionContract access");
    const resolve = await addDoc(
      collection(db, `companies/${companyId}/optionContract`),
      optionContract);
    await updateDoc(resolve, {id: resolve.id});
  },
  // オプション契約更新
  updateOptionContract: async (companyId: string, optionContract: OptionContract): Promise<void> => {
    console.log("companies updateOptionContract access");
    await updateDoc(
      doc(db, `companies/${companyId}/optionContract/${optionContract.id}`),
      optionContract);
  },
};

export default companiesQuery;
